import {
  type messageCallback,
  type InboundMessage,
  type MessageFilter,
} from 'ably';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAblyRealtime } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/hooks/useAblyRealtime';
import { type AblyTokenType } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/types';

export interface UseSubscribeToAblyChannelParams {
  autoConnect?: boolean;
  channel: string;
  enabled?: boolean;
  filter?: Array<string> | MessageFilter | string;
  onMessage: messageCallback<InboundMessage>;
  onResubscribedAfterInactivity?: (() => void) | null;
  onUnsubscribedDueToInactivity?: () => void;
  tokenType: AblyTokenType;
}

export interface UseSubscribeToAblyChannelReturn {
  subscribe: () => Promise<void>;
  unsubscribe: () => void;
}

type UseSubscribeToAblyChannel = (
  params: UseSubscribeToAblyChannelParams,
) => UseSubscribeToAblyChannelReturn;

/**
 * Utility hook that manages connection to the ably client and subscription to a channel.
 *
 * By default it will automatically connect and subscribe to the channel when the component mounts
 * but you can disable this behavior by setting `autoConnect` to false and control the
 * subscription manually using the `subscribe` and `unsubscribe` functions returned by the hook.
 *
 * If you want to receive only a subset of messages from the channel, you can use the `filter` parameter.
 *
 * In order to save costs, ably will automatically be disconnected after some time if the page becomes
 * inactive. While ably is disconnected, you WILL NOT receive any of messages published to the channel and
 * they WILL NOT be available once the connection is re-established. It is your responsibility to implement
 * proper fallback mechanisms via the onUnsubscribedDueToInactivity and onResubscribedAfterInactivity handlers
 * (by default we will show a toast prompting the user to refresh the page when we become active again after
 * a period of inactivity).
 *
 * @param {AblyTokenType} tokenType - Determines which authUrl to use
 * @param {string} channel The name of the channel to subscribe to
 * @param {messageCallback<InboundMessage>} onMessage The message handler that will be called when a message is received
 * @param {boolean} [enabled=true] Let you manually enable/disable the subscription
 * @param {boolean} [autoConnect=true] If TRUE, the subscription will be automatically started when the component mounts
 * @param {string | string[] | MessageFilter} filter The message handler will only be called for messages that match the filter
 *        (see https://ably.com/docs/channels?lang=javascript#subscription-filters)
 * @param {() => void} onUnsubscribedDueToInactivity Called when ably is disconnected due to
 *        the page becoming inactive for too long
 * @param {(() => void) | null} onResubscribedAfterInactivity Called when ably is reconnected when
 *        an inactive page becomes active again
 * @returns {UseSubscribeToAblyChannelReturn}
 * - subscribe - Manually subscribe to the channel and register the provided message handler
 * - unsubscribe - Manually unsubscribe from the channel and detach the channel if there are no more subscriptions
 */
export const useSubscribeToAblyChannel: UseSubscribeToAblyChannel = ({
  tokenType,
  channel,
  onMessage,
  enabled = true,
  autoConnect = true,
  filter,
  onUnsubscribedDueToInactivity,
  onResubscribedAfterInactivity,
}) => {
  const { registerMessageHandler, unregisterMessageHandler, isAblyEnabled } =
    useAblyRealtime({
      tokenType,
    });

  const handlerId = useMemo(
    () => `${channel}-${Math.random().toString(36).substring(2, 11)}`,
    [channel],
  );

  const isSubscribedRef = useRef(false);

  /**
   * Subscribes to the channel with the given filter and message handler
   */
  const subscribe = useCallback(async () => {
    if (!isAblyEnabled || !enabled || isSubscribedRef.current) {
      return;
    }

    await registerMessageHandler({
      id: handlerId,
      channel,
      filter,
      callback: onMessage,
      onUnsubscribedDueToInactivity,
      onResubscribedAfterInactivity,
    });

    isSubscribedRef.current = true;
  }, [
    channel,
    enabled,
    filter,
    handlerId,
    isAblyEnabled,
    onMessage,
    onResubscribedAfterInactivity,
    onUnsubscribedDueToInactivity,
    registerMessageHandler,
  ]);

  /**
   * Unsubscribes this specific message handler from the channel and detaches
   * the channel if there are no more subscriptions to it
   */
  const unsubscribe = useCallback(() => {
    if (!isSubscribedRef.current) {
      return;
    }

    unregisterMessageHandler(handlerId);
    isSubscribedRef.current = false;
  }, [handlerId, unregisterMessageHandler]);

  useEffect(() => {
    if (!isAblyEnabled) {
      return;
    }

    if (autoConnect) {
      subscribe();
    }

    return () => {
      unsubscribe();
    };
  }, [autoConnect, isAblyEnabled, subscribe, unsubscribe]);

  return { subscribe, unsubscribe };
};
